import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { validate as isUuid } from 'uuid';
import { ALPHA_ID_PREFIXES, isValidAlphaID } from '@theguarantors/tg-modern-business';

import { routes } from '@/constants/Constants';
import useABTestStore from '@/stores/abTestStore';
import { useAnalyticsContext } from '@/shared/providers/analytics';
import { cachedSessionStorage } from '@/utils/cachedSessionStorage';
import { PrivateRoutes } from './PrivateRoutes';
import { PublicRoutes } from './PublicRoutes';

export const findRouteByPath = (pathname: string) =>
  [...PublicRoutes, ...PrivateRoutes].find((route) => {
    if (route.path === pathname) return true;

    if (route.path === routes.userDetails) {
      const userId = pathname.split('/')[2];
      return Boolean(userId && isUuid(userId));
    }

    if (route.path === routes.claimsSubmissionDetails) {
      const segments = pathname.split('/');
      const claimAlphaId = segments[2];
      const claimSubmissionAlphaId = segments[4];

      return Boolean(
        claimAlphaId &&
        claimSubmissionAlphaId &&
        isValidAlphaID(ALPHA_ID_PREFIXES.CLAIM, claimAlphaId) &&
        isValidAlphaID(ALPHA_ID_PREFIXES.CLAIM_SUBMISSION, claimSubmissionAlphaId),
      );
    }

    return false;
  });

export const RouteEffects = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { page, isReady } = useAnalyticsContext();
  const { loaded: experimentsLoaded } = useABTestStore();
  useABTestStore.getState().useFetchABTestsValues();

  useEffect(() => {
    cachedSessionStorage.cacheStorage();
    cachedSessionStorage.storeUTM(searchParams);
  }, [searchParams]);

  useEffect(() => {
    if (!isReady || !experimentsLoaded) return;

    window.scrollTo({ top: 0, behavior: 'instant' });
    if (!location.pathname || location.pathname === routes.home) return;

    page(location.pathname + location.hash);

    const matchedRoute = findRouteByPath(location.pathname);

    if (matchedRoute?.title && document.title !== matchedRoute.title) {
      document.title = matchedRoute.title;
    }
  }, [experimentsLoaded, isReady, location.hash, location.pathname, page]);

  return null;
};
